import { useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { Drawer } from "components/ui/drawer/Drawer"
import { Menu } from "components/ui/menu/Menu"
import { useDrawing, useDrawingWithProcessings } from "hooks/api/drawings/drawings.hook"
import { useDrawerStore, useGeneralFeedbackStore } from "store/drawer.store"
import { useViewerStore } from "store/viewer.store"
import { useChatStore } from "store/aiAgent.store"
import { parseFileSizeInMB } from "helpers/utils.helper"
import { Header } from "./layout/Header/Header"
import { Documents } from "./layout/Documents/Documents"
import { Configurations } from "./layout/Configurations/Configurations"
import { Versions } from "./layout/Versions/Versions"
import { FilePartiallySupported } from "./layout/FilePartiallySupported/FilePartiallySupported"
import { SpecificationKeywords } from "./layout/FoundItems/layout/SpecificationKeywords/SpecificationKeywords"
import { DrawingKeywords } from "./layout/FoundItems/layout/DrawingKeywords/DrawingKeywords"
import { FoundItemsObjects } from "./layout/FoundItems/layout/FoundItemsObjects/FoundItemsObjects"
import { ConfigurationSpecificationKeywords } from "./layout/Configurations/layout/ConfigurationSpecificationKeywords/ConfigurationSpecificationKeywords"
import { ConfigurationDrawingKeywords } from "./layout/Configurations/layout/ConfigurationDrawingKeywords/ConfigurationDrawingKeywords"
import { ConfigurationsObjects } from "./layout/Configurations/layout/ConfigurationsObjects/ConfigurationsObjects"
import { MenuItems } from "./MenuItems"
import { Quotations } from "./layout/Quotations/Quotations"
import { useCurrentUserStore } from "store/user.store"
import type { MenuProps } from "antd"
import style from "./FileInfo.module.scss"

const maxFileSizeMB = Number(import.meta.env.VITE_MAX_FILE_SIZE_MB) || 500

const { fileInfo, wrapper, content, partiallySupportedBlock, headerAndContent, menuWrapper, drawer, menuStyles } = style

export const FileInfo = () => {
	const { isDrawerOpen, setDrawerClose, setDrawerOpen } = useDrawerStore()
	const isQuotationViewerOpen = useViewerStore((state) => state.isQuotationViewerOpen)
	const { setGeneralFeedback } = useGeneralFeedbackStore()
	const { clearChatHistory, closeChat } = useChatStore()
	const { currentUser } = useCurrentUserStore()

	const [searchParams, setSearchParams] = useSearchParams()
	const drawingId = Number(searchParams.get("drawingID"))
	const processingId = Number(searchParams.get("processingID"))
	const tabFromUrl = searchParams.get("tab") || "documents"

	const { data: drawing, isPending: isDrawingPending } = useDrawing(drawingId)
	const { data: drawingWithProcessings } = useDrawingWithProcessings(drawingId, processingId)

	const statusDrawingWithProcessing = drawingWithProcessings?.status
	const drawingStatus = drawing?.status
	const finalDrawingStatus = drawingStatus !== "uploaded" ? statusDrawingWithProcessing : drawingStatus // Status of current drawing

	const currentFileSize = parseFileSizeInMB(drawing?.size)
	const isFileSizeExceeded = currentFileSize > maxFileSizeMB
	const isFileNotSupported = drawing?.supportedStatus === "not_supported"

	const menuItems = MenuItems(finalDrawingStatus, currentUser.role)

	// Fetch documents and drawer info
	useEffect(() => {
		if (drawingId) setDrawerOpen()
	}, [drawingId, setDrawerOpen])

	// Rreset drawer ID and close the drower
	const closeDrawerHandle = () => {
		setDrawerClose() // Close the drawer
		setSearchParams({}) // Reset all Search Params
		setGeneralFeedback("") // Reset general feedback
		clearChatHistory() // Clear chat history
		closeChat() // Close chat
		document.title = "Stevens | Archiscan" // Reset document title
	}

	const filePartiallySupportedText = () => {
		const messages = []

		if (isFileNotSupported) {
			messages.push(
				"This file format is partially supported. The interactions and searches within the file will yield results with low accuracy and quality.",
			)
		}

		if (isFileSizeExceeded) {
			messages.push(`File size exceeds ${maxFileSizeMB}MB. This may cause failure when processing the file.`)
		}

		return messages.join(" Also, the ")
	}

	const onClickMenuHandle: MenuProps["onClick"] = (e) => {
		const key = e.key

		const newSearchParams = new URLSearchParams(searchParams)
		newSearchParams.set("tab", key)
		setSearchParams(newSearchParams)
	}

	// Wrapper for menu items that need the standard header
	const withHeaderAndContent = (children: JSX.Element) => (
		<div className={headerAndContent}>
			<Header
				onCloseDrawer={() => closeDrawerHandle()}
				finalDrawingStatus={finalDrawingStatus}
				drawingName={drawing?.name}
				isDrawingPending={isDrawingPending}
			/>
			<div className={`DrawerTabsContent ${content}`}>{children}</div>
		</div>
	)

	const MENU_CONTENT_MAP: Record<string, JSX.Element> = {
		documents: withHeaderAndContent(<Documents drawingStatus={drawingStatus} drawingOwner={drawing?.owner} />),
		"specification-keywords": withHeaderAndContent(<SpecificationKeywords drawingStatus={finalDrawingStatus} />),
		"drawing-keywords": withHeaderAndContent(<DrawingKeywords drawingStatus={finalDrawingStatus} />),
		objects: withHeaderAndContent(<FoundItemsObjects drawingStatus={finalDrawingStatus} />),
		"configuration-specification-keywords": withHeaderAndContent(
			<ConfigurationSpecificationKeywords drawingStatus={finalDrawingStatus} />,
		),
		"configuration-drawing-keywords": withHeaderAndContent(
			<ConfigurationDrawingKeywords drawingStatus={finalDrawingStatus} />,
		),
		"specification-objects": withHeaderAndContent(<ConfigurationsObjects drawingStatus={finalDrawingStatus} />),
		configurations: withHeaderAndContent(<Configurations drawingStatus={finalDrawingStatus} />),
		versions: withHeaderAndContent(<Versions drawingStatus={finalDrawingStatus} />),
		quotations: (
			<Quotations
				onCloseDrawer={() => closeDrawerHandle()}
				drawingName={drawing?.name}
				drawingStatus={drawingStatus}
				fileOwner={drawing?.owner}
			/>
		),
	}

	const renderContent = () => {
		return MENU_CONTENT_MAP[tabFromUrl] ?? MENU_CONTENT_MAP.documents
	}

	return (
		<Drawer
			width={1300}
			open={isDrawerOpen}
			onClose={() => closeDrawerHandle()}
			// While the fullscreen quotation viewer/editor is open it owns Escape (close the right
			// sidebar). Keep rc-drawer from also handling Escape — it stopPropagations the key and
			// would both close this drawer and swallow it from the editor.
			keyboard={!isQuotationViewerOpen}
			className={drawer}
			styles={{
				body: { backgroundColor: "var(--clr-superLightGray)", padding: 0 },
				content: { borderRadius: "16px", margin: "8px 0" },
			}}
		>
			<div className={fileInfo}>
				<div className={partiallySupportedBlock}>
					{/* If the file is not supported or size exceeded, show the partially supported component */}
					{isFileNotSupported || isFileSizeExceeded ? (
						<FilePartiallySupported text={filePartiallySupportedText()} />
					) : null}
				</div>
				<div className={wrapper}>
					<div className={menuWrapper}>
						<Menu
							items={menuItems}
							onClick={onClickMenuHandle}
							mode="inline"
							inlineCollapsed={false}
							defaultOpenKeys={["found-items", "configurations"]}
							selectedKeys={[tabFromUrl]}
							className={menuStyles}
						/>
					</div>
					{renderContent()}
				</div>
			</div>
		</Drawer>
	)
}
